import { Component, effect, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { Navigation } from 'app/core/navigation/navigation.types';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { User } from 'app/core/user/user.types';
import { UserServiceHeader } from 'app/core/user/user.service';
import { AppState } from 'app/shared/common/utils/AppState';
import { UtilCommon } from 'app/shared/common/utils/UtilCommon';
import { COLOR_BREADCUMENU, FONT_WEIGHT_BREADCUMENU, IBreadcumMenuItem } from 'app/shared/common/utils/breadcumMenu.service';

@Component({
    selector: 'classy-layout',
    templateUrl: './classy.component.html',
    styleUrl: './classy.component.scss',
    encapsulation: ViewEncapsulation.None
})
export class ClassyLayoutComponent implements OnInit, OnDestroy {
    isScreenSmall: boolean;
    navigation: Navigation;
    user: User;
    isOpened: boolean = true;
    isOpenedChanged: boolean = false;
    titlePage: string = 'WELCOME_NEW';
    colorTitlePage: string = '#687588';
    countHeader: string = '';
    urlHeader: string = '';
    subMenu: IBreadcumMenuItem[] = [];
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _navigationService: NavigationService,
        private _userService: UserServiceHeader,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _appState: AppState
    ) {
        effect(() => {
            if (this._appState?.breadcumMenu() && UtilCommon.isArray(this._appState?.breadcumMenu())) {
                this.titlePage = '';
                this.subMenu = [];
                if (this._appState?.breadcumMenu().length === 1) {
                    this.titlePage = this._appState?.breadcumMenu()[0].title;
                    this.colorTitlePage = this._appState?.breadcumMenu()[0].color ?? COLOR_BREADCUMENU.Default;
                    this.countHeader = this._appState?.breadcumMenu()[0].count;
                    this.urlHeader = this._appState?.breadcumMenu()[0]?.url;
                    this.subMenu = [];
                    return
                }
                for (let index = 0; index < this._appState?.breadcumMenu().length; index++) {
                    if (index === 0) {
                        this.titlePage = this._appState?.breadcumMenu()[index].title;
                        this.colorTitlePage = this._appState?.breadcumMenu()[index].color ?? COLOR_BREADCUMENU.Default;
                        this.countHeader = this._appState?.breadcumMenu()[index].count;
                        this.urlHeader = this._appState?.breadcumMenu()[index]?.url;
                    } else {
                        this.subMenu.push({
                            title: this._appState?.breadcumMenu()[index].title,
                            url: this._appState?.breadcumMenu()[index].url,
                            color: this._appState?.breadcumMenu()[index].color ?? COLOR_BREADCUMENU.Gray600,
                            fontWeight: this._appState?.breadcumMenu()[index].fontWeight ?? FONT_WEIGHT_BREADCUMENU.F400,
                            count: this._appState?.breadcumMenu()[index].count
                        });
                    }
                }
            }
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        const userInfo = this._appState.getAppState();
        const tenantCode = userInfo?.moreInfo?.tenantCode;
        this.imageTenant = `assets/images/logo/logo_${tenantCode}.png`; // Fallback image
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        // Subscribe to the user service
        this._userService.user$
            .pipe((takeUntil(this._unsubscribeAll)))
            .subscribe((user: User) => {
                this.user = user;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {

                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);
        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
            if (!this.isOpenedChanged) {
                this.isOpenedChanged = true;
                this.isOpened = false;
                navigation.openedChanged
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe((data) => {
                        this.isOpened = data;
                    });
            }
        }
    }

    imageTenant = 'assets/images/logo/logo.png';
    onImageError(event: Event) {
        const target = event.target as HTMLImageElement;
        target.src = 'assets/images/logo/logo.png'; // Fallback image
        console.error('Image not found, loading fallback image.');
    }
}
